import { computed, effect, inject, untracked } from '@angular/core';
import type { DocumentType, FactoryDocument } from '@indutrust/factory-model';
import { withCrudEntitiesEvents, withFileUpload } from '@indutrust/shared-util';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { injectDispatch } from '@ngrx/signals/events';
import { FactoryDocumentApiClient } from '../factory-document-api-client.service';
import { factoryDocumentCrudEvents } from './factory-documents.events';

type DocumentUploadContext = {
  uploadCategory: DocumentType;
  uploadFactoryId: number | null;
};

type ContextStore = {
  uploadCategory: () => DocumentType;
  uploadFactoryId: () => number | null;
};

/**
 * Component-scoped store for the factory-scoped `/documents` sub-resource.
 *
 * Provided by {@link provideFactoryEditStores} so each factory editor instance
 * gets a fresh entity collection.
 *
 * Callers MUST pass `urlSegments: { factoryId }` to every CRUD method —
 * the wrapped {@link FactoryDocumentApiClient} substitutes the segment
 * into the URL template before issuing the request.
 *
 * Binary uploads use {@link withFileUpload}: call `prepareUpload()` with the
 * factoryId + category, then `stageFiles()` + `commit()`. Files upload
 * sequentially with per-file status; the list reloads automatically once
 * all uploads settle without errors.
 *
 * @example
 * const documentsStore = inject(FactoryDocumentsStore);
 * documentsStore.prepareUpload(42, 'trade_license');
 * documentsStore.stageFiles([file]);
 * documentsStore.commit();
 */
export const FactoryDocumentsStore = signalStore(
  withState<DocumentUploadContext>({
    uploadCategory: 'trade_license',
    uploadFactoryId: null,
  }),
  withComputed(({ uploadFactoryId }) => ({
    canUploadDocuments: computed(
      () =>
        typeof uploadFactoryId() === 'number' && (uploadFactoryId() ?? 0) > 0
    ),
  })),
  withMethods((store) => ({
    /**
     * Sets the factoryId + category context used by the next `commit()`.
     * Call this before `stageFiles()`.
     */
    prepareUpload(factoryId: number, category: DocumentType): void {
      patchState(store, {
        uploadCategory: category,
        uploadFactoryId: factoryId,
      });
    },
  })),
  withFileUpload<FactoryDocument>({
    upload: (store) => {
      const client = inject(FactoryDocumentApiClient);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const ctx = store as ContextStore;
      return (file: File) => {
        const factoryId = ctx.uploadFactoryId();
        const category = ctx.uploadCategory();
        return client.upload(factoryId ?? 0, file, category);
      };
    },
  }),
  withCrudEntitiesEvents<FactoryDocument>(factoryDocumentCrudEvents, () =>
    inject(FactoryDocumentApiClient)
  ),
  withHooks((store) => {
    const dispatchCrud = injectDispatch(factoryDocumentCrudEvents);
    return {
      onInit() {
        /**
         * Re-fetches the document list after all uploads have settled
         * successfully. Suppressed on initial state (no staged items).
         */
        effect(() => {
          const settled = store.uploadSettled();
          const factoryId = store.uploadFactoryId();
          const hasSuccesses = store
            .uploadStagedItems()
            .some((i) => i.status === 'success');
          if (settled && factoryId !== null && hasSuccesses) {
            untracked(() => {
              dispatchCrud.loadAllRequested({
                urlSegments: { factoryId },
              });
            });
          }
        });
      },
    };
  })
);
